import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import { getWorkOrderPrintView } from "src/api/Service/WorkOrders"

import { useToast } from "src/hooks/use_toast"

import CustomerCopyDocument from "./CustomerCopyDocument"
import TechWorksheetDocument from "./TechWorksheetDocument"

const DOCUMENTS = {
  customer: CustomerCopyDocument,
  tech: TechWorksheetDocument,
}

const WorkOrderPrintMount = ({ version, workOrderSequentialId, onDone }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  const { data, isSuccess, isError } = useQuery({
    queryKey: ["workOrderPrintView", marinaSlug, workOrderSequentialId],
    queryFn: () => getWorkOrderPrintView(marinaSlug, workOrderSequentialId),
    enabled: Boolean(version),
    cacheTime: 0,
  })

  const Document = version && DOCUMENTS[version]

  useEffect(() => {
    if (!version || !Document || !isSuccess) return undefined
    document.body.classList.add("work-order-printing")
    const timer = window.setTimeout(() => {
      window.print()
      onDoneRef.current()
    }, 0)
    return () => {
      window.clearTimeout(timer)
      document.body.classList.remove("work-order-printing")
    }
  }, [version, Document, isSuccess])

  useEffect(() => {
    if (!version || !isError) return
    showToast("Failed to load the print document. Please try again.", {
      type: "error",
    })
    onDoneRef.current()
  }, [version, isError, showToast])

  if (!Document || !isSuccess) return null

  return createPortal(
    <div id="work-order-print-root" className="hidden print:block">
      <Document printView={data} />
    </div>,
    document.body
  )
}

WorkOrderPrintMount.propTypes = {
  version: PropTypes.oneOf(["customer", "tech"]),
  workOrderSequentialId: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
}

export default WorkOrderPrintMount
